import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  Alert
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import SortIcon from '@mui/icons-material/Sort';
import { useTranslation } from 'react-i18next';

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  sortable?: boolean;
  description?: string;
}

interface InventoryTableSettingsProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: TableColumn[]) => void;
  currentColumns: TableColumn[];
}

const InventoryTableSettings: React.FC<InventoryTableSettingsProps> = ({
  open,
  onClose,
  onSave,
  currentColumns
}) => {
  const { t } = useTranslation('accounting');
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [showInfoBox, setShowInfoBox] = useState(false);

  useEffect(() => {
    // Tabloda güncellemeleri izole etmek için derin kopyalama
    setColumns(JSON.parse(JSON.stringify(currentColumns)));
  }, [currentColumns, open]);

  useEffect(() => {
    if (open) {
      setShowInfoBox(false);
    }
  }, [open]);

  // Görünürlüğü değiştirme
  const handleToggleVisibility = (id: string) => {
    setColumns(prevColumns =>
      prevColumns.map(column => {
        // Sabit sütunlar her zaman görünür olmalı
        if (column.id === id && !column.fixed) {
          const newVisible = !column.visible;
          return { 
            ...column, 
            visible: newVisible,
            // Görünürlük kapatılırsa sıralama da kapatılsın
            ...(newVisible ? {} : { sortable: false })
          };
        }
        return column;
      })
    );
  };

  // Sıralanabilirliği değiştirme
  const handleToggleSortable = (id: string) => {
    // Sıralanmaması gereken sütunlar
    const nonSortableIds = ['actions', 'rowNumber', 'ecommerce', 'qrmenu', 'canBeNegative'];
    setColumns(prevColumns =>
      prevColumns.map(column => {
        if (column.id === id && !nonSortableIds.includes(column.id)) {
          return { ...column, sortable: !column.sortable };
        }
        return column;
      })
    );
  };

  // Sıralama desteklemeyen sütunlar
  const nonSortableIds = ['actions', 'rowNumber', 'ecommerce', 'qrmenu', 'canBeNegative'];

  // Değişiklikleri kaydetme
  const handleSave = () => {
    onSave(columns);
    onClose();
  };

  // İptal etme
  const handleCancel = () => {
    onClose();
  };

  // Sütunları gruplama: sabit ve değiştirilebilir olanlar
  const fixedColumns = columns.filter(column => column.fixed);
  const customizableColumns = columns.filter(column => !column.fixed);
  const getLocalizedColumnLabel = (column: TableColumn) => {
    const keyMap: Record<string, string> = {
      rowNumber: 'rowNumber',
      name: 'name',
      barcode: 'barcode',
      stockCode: 'stockCode',
      category: 'category',
      brand: 'brand',
      branch: 'branch',
      quantity: 'quantity',
      unit: 'unit',
      salePrice: 'salePrice',
      purchasePrice: 'purchasePrice',
      vatRate: 'vatRate',
      vatStatus: 'vatStatus',
      sctRate: 'sctRate',
      sctStatus: 'sctStatus',
      warehouse: 'warehouse',
      location: 'location',
      description: 'description',
      ecommerce: 'ecommerce',
      qrmenu: 'qrmenu',
      departmentNo: 'departmentNo',
      canBeNegative: 'canBeNegative',
      actions: 'actions'
    };
    const mappedKey = keyMap[column.id];
    return mappedKey ? t(`stock.table.columns.${mappedKey}`) : column.label;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ 
        sx: { 
          borderRadius: 2,
        } 
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          fontWeight: 'bold',
          py: 2
        }}
      >
        {t('stock.tableSettings.title')}
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={t('stock.tableSettings.infoBox.tooltip')}>
            <IconButton
              size="small"
              onClick={() => setShowInfoBox(prev => !prev)}
              sx={{
                color: showInfoBox ? 'primary.main' : 'text.secondary',
                border: '1px solid',
                borderColor: showInfoBox ? 'primary.main' : 'divider',
                borderRadius: 1
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {showInfoBox && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('stock.tableSettings.infoBox.title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {t('stock.tableSettings.infoBox.description')}
            </Typography>
            <Typography variant="body2">{t('stock.tableSettings.infoBox.item1')}</Typography>
            <Typography variant="body2">{t('stock.tableSettings.infoBox.item2')}</Typography>
            <Typography variant="body2">{t('stock.tableSettings.infoBox.item3')}</Typography>
          </Alert>
        )}
        
        {/* Sabit Sütunlar */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1 }} /> {t('stock.tableSettings.fixedColumns')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('stock.tableSettings.fixedColumnsHint')}
          </Typography>
          
          <Grid container spacing={2}>
            {fixedColumns.map((column) => (
              <Grid item xs={12} sm={6} md={4} key={column.id}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'primary.contrastText', mr: 1, opacity: 0.7 }} />
                      <Typography variant="body1">{getLocalizedColumnLabel(column)}</Typography>
                    </Box>
                    {column.description && (
                      <Tooltip title={column.description}>
                        <IconButton size="small" sx={{ color: 'primary.contrastText' }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, pl: 3.5 }}>
                    <FormControlLabel
                      control={<Switch checked={true} disabled size="small" />}
                      label={<Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>{t('stock.tableSettings.visible')}</Typography>}
                      sx={{ margin: 0 }}
                    />
                    {!nonSortableIds.includes(column.id) && (
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={!!column.sortable}
                            onChange={() => handleToggleSortable(column.id)}
                            size="small"
                            sx={{ 
                              '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.5)' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <SortIcon sx={{ fontSize: 14, color: 'primary.contrastText', opacity: 0.9 }} />
                            <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>{t('stock.tableSettings.sort')}</Typography>
                          </Box>
                        }
                        sx={{ margin: 0 }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
        
        {/* Özelleştirilebilir Sütunlar */}
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <DragIndicatorIcon sx={{ mr: 1 }} /> {t('stock.tableSettings.customizableColumns')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('stock.tableSettings.customizableColumnsHint')}
          </Typography>
          
          <Grid container spacing={2}>
            {customizableColumns.map((column) => (
              <Grid item xs={12} sm={6} md={4} key={column.id}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: column.visible ? 'primary.light' : 'divider',
                    borderRadius: 1,
                    bgcolor: column.visible ? 'rgba(0, 0, 0, 0.04)' : 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1, opacity: 0.7 }} />
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: column.visible ? 'text.primary' : 'text.secondary',
                          fontWeight: column.visible ? 'medium' : 'normal'
                        }}
                      >
                        {getLocalizedColumnLabel(column)}
                      </Typography>
                    </Box>
                    {column.description && (
                      <Tooltip title={column.description}>
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, pl: 3.5 }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={column.visible}
                          onChange={() => handleToggleVisibility(column.id)}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption" color="text.secondary">{t('stock.tableSettings.visible')}</Typography>}
                      sx={{ margin: 0 }}
                    />
                    {!nonSortableIds.includes(column.id) && (
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={!!column.sortable}
                            onChange={() => handleToggleSortable(column.id)}
                            size="small"
                            color="secondary"
                            disabled={!column.visible}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <SortIcon sx={{ fontSize: 14, color: column.visible ? 'text.secondary' : 'text.disabled' }} />
                            <Typography variant="caption" color={column.visible ? 'text.secondary' : 'text.disabled'}>{t('stock.tableSettings.sort')}</Typography>
                          </Box>
                        }
                        sx={{ margin: 0 }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleCancel}
          variant="outlined"
        >
          {t('stock.common.cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {t('stock.tableSettings.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryTableSettings; 